/**
 * Inline SVG icon set for the Resales-Online landing prototype.
 *
 * The Figma frame references icons as flattened font-vectors / exported SVGs that
 * do not translate cleanly into a CRA bundle, so per the asset-handling brief we
 * use lightweight inline SVG fallbacks here (stroke-based, currentColor driven).
 * Every icon accepts a `size` (default 20) and spreads remaining props.
 */
import React from 'react';

const base = (size) => ({
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
});

export const IconX = ({ size = 20, ...p }) => (
    <svg {...base(size)} {...p}><path d="M18 6 6 18M6 6l12 12" /></svg>
);

export const IconKey = ({ size = 20, ...p }) => (
    <svg {...base(size)} {...p}>
        <circle cx="7.5" cy="15.5" r="5.5" />
        <path d="m21 2-9.6 9.6M15.5 7.5l3 3L22 7l-3-3" />
    </svg>
);

export const IconHeart = ({ size = 20, fill = 'none', ...p }) => (
    <svg {...base(size)} fill={fill} {...p}>
        <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 1 0-7.8 7.8l1 1.1L12 21l7.8-7.5 1-1.1a5.5 5.5 0 0 0 0-7.8Z" />
    </svg>
);

export const IconPhone = ({ size = 20, ...p }) => (
    <svg {...base(size)} {...p}>
        <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3-8.6A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.4-1.2a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2Z" />
    </svg>
);

export const IconChevronDown = ({ size = 20, ...p }) => (
    <svg {...base(size)} {...p}><path d="m6 9 6 6 6-6" /></svg>
);

export const IconChevronLeft = ({ size = 20, ...p }) => (
    <svg {...base(size)} {...p}><path d="m15 18-6-6 6-6" /></svg>
);

export const IconChevronRight = ({ size = 20, ...p }) => (
    <svg {...base(size)} {...p}><path d="m9 18 6-6-6-6" /></svg>
);

export const IconBed = ({ size = 20, ...p }) => (
    <svg {...base(size)} {...p}>
        <path d="M2 4v16M2 8h18a2 2 0 0 1 2 2v10M2 17h20" />
        <path d="M6 8V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
);

export const IconBath = ({ size = 20, ...p }) => (
    <svg {...base(size)} {...p}>
        <path d="M4 12V5a2 2 0 0 1 2-2 2 2 0 0 1 2 2M4 12h16a1 1 0 0 1 1 1v2a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4v-2a1 1 0 0 1 1-1Z" />
        <path d="M6 19v2M18 19v2M7 5h2" />
    </svg>
);

export const IconMapPin = ({ size = 20, ...p }) => (
    <svg {...base(size)} {...p}>
        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
        <circle cx="12" cy="10" r="3" />
    </svg>
);

export const IconGlobe = ({ size = 20, ...p }) => (
    <svg {...base(size)} {...p}>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3c2.5 2.6 2.5 15.4 0 18M12 3c-2.5 2.6-2.5 15.4 0 18" />
    </svg>
);

export const IconExpand = ({ size = 20, ...p }) => (
    <svg {...base(size)} {...p}><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></svg>
);

// Social marks — full brand colours per each platform's own guidelines (not our
// line-icon style) so they read as recognisable logos. Each fills explicitly and
// ignores currentColor; Instagram carries its signature corner gradient.
export const IconFacebook = ({ size = 20, ...p }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...p}>
        <path fill="#1877F2" d="M13.5 21v-8h2.69l.4-3.12H13.5V7.9c0-.9.25-1.52 1.54-1.52h1.65V3.59c-.29-.04-1.27-.13-2.41-.13-2.39 0-4.02 1.46-4.02 4.13v2.29H7.56V13h2.71v8h3.23z" />
    </svg>
);

export const IconInstagram = ({ size = 20, ...p }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...p}>
        <defs>
            <radialGradient id="rsIgGrad" cx="0.3" cy="1.05" r="1.15">
                <stop offset="0" stopColor="#FED576" />
                <stop offset="0.26" stopColor="#F47133" />
                <stop offset="0.55" stopColor="#BC3081" />
                <stop offset="1" stopColor="#4C63D2" />
            </radialGradient>
        </defs>
        <rect x="2" y="2" width="20" height="20" rx="6" fill="url(#rsIgGrad)" />
        <rect x="6.4" y="6.4" width="11.2" height="11.2" rx="3.4" fill="none" stroke="#fff" strokeWidth="1.7" />
        <circle cx="12" cy="12" r="3.1" fill="none" stroke="#fff" strokeWidth="1.7" />
        <circle cx="16.5" cy="7.5" r="1.15" fill="#fff" />
    </svg>
);

export const IconLinkedIn = ({ size = 20, ...p }) => (
    // viewBox offset re-centres the "in" glyph (it spans ~x0-21.5, y1-21) in the box.
    <svg width={size} height={size} viewBox="-1.25 -1 24 24" fill="none" {...p}>
        <path fill="#0A66C2" d="M4.98 3.5A2.5 2.5 0 1 1 0 3.5a2.5 2.5 0 0 1 4.98 0zM.25 8.02h4.46V21H.25V8.02zM7.6 8.02h4.28v1.77h.06c.6-1.13 2.06-2.32 4.24-2.32 4.53 0 5.37 2.98 5.37 6.86V21h-4.46v-5.77c0-1.38-.03-3.15-1.92-3.15-1.93 0-2.22 1.5-2.22 3.05V21H7.6V8.02z" />
    </svg>
);

export const IconTwitterX = ({ size = 20, ...p }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...p}>
        <path fill="#000000" d="M18.24 2.25h3.31l-7.23 8.26L22.83 21.75h-6.66l-5.21-6.82-5.98 6.82H1.68l7.73-8.84L1.25 2.25H8.08l4.71 6.23 5.45-6.23zm-1.16 17.52h1.83L7.09 4.13H5.12l11.96 15.64z" />
    </svg>
);

export const IconSend = ({ size = 20, ...p }) => (
    <svg {...base(size)} {...p}><path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7Z" /></svg>
);

export const IconCheck = ({ size = 20, ...p }) => (
    <svg {...base(size)} {...p}><path d="M20 6 9 17l-5-5" /></svg>
);

export const IconClock = ({ size = 20, ...p }) => (
    <svg {...base(size)} {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
);

export const IconMail = ({ size = 20, ...p }) => (
    <svg {...base(size)} {...p}>
        <path d="M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z" />
        <path d="m3 7 9 6 9-6" />
    </svg>
);
